/**
 * Coup de pouce — Attendu 1050 « lire l'abscisse d'un point sur une droite
 * graduée en tiers, quarts, moitiés, dixièmes ». UNE page STATIQUE partagée
 * par les six generator_keys (cadrage prof 2026-09-08, version simple).
 *
 * Une seule idée par élément, chaque chiffre de la fraction relié à son
 * comptage par la couleur, rien d'autre. Cinq éléments, un seul mot (parts) :
 *   1. la demi-droite de 0 à 2 en tiers, A placé à 5/3 ;
 *   2. sous la droite, entre 0 et 1, un trait bleu « 1 unité = 3 parts » ;
 *   3. au-dessus, cinq petits arcs orange de 0 à A, numérotés 1 à 5 : l'arc
 *      dit « intervalle », l'élève ne peut compter que des parts, jamais
 *      des traits (l'erreur principale, corrigée par la forme) ;
 *   4. en gros : A = 5/3, le 5 orange (« 5 parts depuis 0 »), le 3 bleu
 *      (« 3 parts dans une unité »), deux légendes attachées aux chiffres ;
 *   5. la règle, deux lignes : « Dénominateur : le nombre de parts dans une
 *      unité. » « Numérateur : le nombre de parts depuis 0. » (le mot exact
 *      qu'il connaît, plutôt que « en bas / en haut »).
 * Supprimés (version précédente illisible) : accolade et pointillés sur les
 * traits, cases pleines, lignes intermédiaires, « c'est aussi 1 + 2/3 » (le
 * nombre mixte vit au Vrai/Faux). Trois regards : le bleu dit combien de
 * parts dans une unité, l'orange combien de parts jusqu'à A, la fraction
 * assemble les deux.
 *
 * POINT SACRIFIÉ : 5/3 ne sort d'aucune carte (gen_1050, auto-testé par
 * cdp1050.self.test.js). Largeur bornée à 340 px, tailles de 1048.
 */

const ORANGE = '#EA580C';
const BLEU = '#2563EB';
const ENCRE = '#1E293B';

/** La demi-droite : 0 → 2, 160 px par unité, tiers. */
const X0 = 40;
const UNITE = 160;
const DEN = 3;
const PART = UNITE / DEN;
const Y = 96;
const toX = (k) => X0 + k * PART;

/** Graduations : 0 à 2 en tiers, entiers plus longs et étiquetés. */
const GRADUATIONS = Array.from({ length: 2 * DEN + 1 }, (_, k) => {
    const x = toX(k);
    const entier = k % DEN === 0;
    const h = entier ? 9 : 5;
    return `<line x1="${x}" y1="${Y - h}" x2="${x}" y2="${Y + h}" stroke="${ENCRE}" stroke-width="${entier ? 2 : 1.2}"/>`
        + (entier ? `<text x="${x}" y="${Y + 24}" font-size="12" font-weight="bold" text-anchor="middle" fill="${ENCRE}">${k / DEN}</text>` : '');
}).join('\n  ');

/** Cinq arcs orange de 0 à A, un par part, numérotés 1 à 5 au-dessus. */
const ARCS = Array.from({ length: 5 }, (_, k) => {
    const x1 = toX(k);
    const x2 = toX(k + 1);
    const xm = (x1 + x2) / 2;
    return `<path d="M ${x1} ${Y - 10} Q ${xm} ${Y - 34} ${x2} ${Y - 10}" fill="none" stroke="${ORANGE}" stroke-width="1.8"/>
  <text x="${xm}" y="${Y - 30}" font-size="10" font-weight="bold" text-anchor="middle" fill="${ORANGE}">${k + 1}</text>`;
}).join('\n  ');

const SVG_PARTS = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 284" width="100%" style="max-width:340px;height:auto;display:block" role="img"
     aria-label="Demi-droite graduée de 0 à 2, l'unité partagée en 3 parts, un point A. Sous la droite, entre 0 et 1 : 1 unité égale 3 parts. Au-dessus, cinq arcs numérotés de 1 à 5, de 0 jusqu'à A. A égale 5 sur 3 : le 5, c'est 5 parts depuis 0 ; le 3, c'est 3 parts dans une unité. Dénominateur : le nombre de parts dans une unité. Numérateur : le nombre de parts depuis 0.">
  <!-- 1. la demi-droite, A placé -->
  <line x1="${X0 - 10}" y1="${Y}" x2="${toX(2 * DEN) + 28}" y2="${Y}" stroke="${ENCRE}" stroke-width="2"/>
  <polygon points="${toX(2 * DEN) + 36},${Y} ${toX(2 * DEN) + 26},${Y - 5} ${toX(2 * DEN) + 26},${Y + 5}" fill="${ENCRE}"/>
  ${GRADUATIONS}
  <circle cx="${toX(5)}" cy="${Y}" r="5" fill="${ORANGE}"/>
  <text x="${toX(5)}" y="${Y + 24}" font-size="12" font-weight="bold" text-anchor="middle" fill="${ORANGE}">A</text>

  <!-- 2. sous la droite, entre 0 et 1 : 1 unité = 3 parts -->
  <line x1="${toX(0)}" y1="${Y + 36}" x2="${toX(DEN)}" y2="${Y + 36}" stroke="${BLEU}" stroke-width="2"/>
  <line x1="${toX(0)}" y1="${Y + 32}" x2="${toX(0)}" y2="${Y + 40}" stroke="${BLEU}" stroke-width="2"/>
  <line x1="${toX(DEN)}" y1="${Y + 32}" x2="${toX(DEN)}" y2="${Y + 40}" stroke="${BLEU}" stroke-width="2"/>
  <text x="${toX(DEN / 2)}" y="${Y + 54}" font-size="12" font-weight="bold" text-anchor="middle" fill="${BLEU}">1 unité = 3 parts</text>

  <!-- 3. au-dessus : cinq arcs de 0 à A -->
  ${ARCS}

  <!-- 4. la fraction, chaque chiffre relié à son comptage par la couleur -->
  <text x="150" y="203" font-size="16" font-weight="bold" text-anchor="end" fill="${ENCRE}">A =</text>
  <text x="166" y="199" font-size="16" font-weight="bold" text-anchor="middle" fill="${ORANGE}">5</text>
  <line x1="156" y1="204" x2="176" y2="204" stroke="${ENCRE}" stroke-width="1.8"/>
  <text x="166" y="221" font-size="16" font-weight="bold" text-anchor="middle" fill="${BLEU}">3</text>
  <text x="186" y="199" font-size="12" fill="${ORANGE}">5 parts depuis 0</text>
  <text x="186" y="221" font-size="12" fill="${BLEU}">3 parts dans une unité</text>

  <!-- 5. la règle, deux lignes (une seule débordait de la fiche) -->
  <text x="200" y="250" font-size="12" text-anchor="middle" fill="${ENCRE}"><tspan font-weight="bold" fill="${BLEU}">Dénominateur</tspan> : le nombre de parts dans une unité.</text>
  <text x="200" y="268" font-size="12" text-anchor="middle" fill="${ENCRE}"><tspan font-weight="bold" fill="${ORANGE}">Numérateur</tspan> : le nombre de parts depuis 0.</text>
</svg>`.trim();

const AIDE = {
    pages: [
        { titre: 'Je compte les parts', svg: SVG_PARTS },
    ],
};

const CP_1050 = {
    flash_texte_trou_saisie: AIDE,
    grad_qcm_unique: AIDE,
    grad_texte_trou_saisie: AIDE,
    lire_qcm_unique: AIDE,
    lire_relier: AIDE,
    lire_tableau_vf: AIDE,
};

export default CP_1050;
